import { useUser } from "@clerk/expo";
import { useRouter } from "expo-router";
import {
  ArrowUpDown,
  Bookmark,
  CheckSquare,
  CircleDot,
  Eye,
  Gauge,
  GitCompareArrows,
  Grid3X3,
  GripHorizontal,
  Lightbulb,
  ListOrdered,
  LucideIcon,
  MessageSquare,
  MessageSquareQuote,
  Package,
  PlaySquare,
  SlidersHorizontal,
  Star,
  Tag,
  Target,
} from "lucide-react-native";
import { FC, useCallback, useMemo } from "react";
import { ScrollView } from "react-native";

import {
  Box,
  Card,
  Header,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionType } from "@/types/utils";
import { getUseCaseSlug, UseCase } from "@/types/utils/useCase";

/* ── Catalog ─────────────────────────────────────────── */

export interface UseCaseEntry {
  description: string;
  icon: LucideIcon;
  /** URL slug used by the `/quick-insights/[useCase]` route (kebab-case). */
  slug: string;
  title: string;
  /** Canonical backend enum value — what we persist in favorites. */
  useCase: UseCase;
}

interface UseCaseStaticEntry {
  descriptionKey: string;
  icon: LucideIcon;
  titleKey: string;
  useCase: UseCase;
}

/**
 * Single source of truth for the quick-insights use cases. Exported so the
 * home screen can render the same list with favorites pulled to the front.
 *
 * Each entry carries both the URL `slug` (derived from `useCase` via
 * `getUseCaseSlug`) and the canonical `UseCase` enum value — the latter is
 * what we store in `user.unsafeMetadata.favorites` so favorites speak the
 * same vocabulary as the rest of the system.
 */
const USE_CASES_STATIC: readonly UseCaseStaticEntry[] = [
  {
    descriptionKey: "preferenceTestDescription",
    icon: GitCompareArrows,
    titleKey: "preferenceTest",
    useCase: UseCase.PreferenceTest,
  },
  {
    descriptionKey: "adCreativeDescription",
    icon: PlaySquare,
    titleKey: "adCreative",
    useCase: UseCase.AdCreative,
  },
  {
    descriptionKey: "awarenessDescription",
    icon: Eye,
    titleKey: "awareness",
    useCase: UseCase.Awareness,
  },
  {
    descriptionKey: "claimTestDescription",
    icon: MessageSquareQuote,
    titleKey: "claimTest",
    useCase: UseCase.ClaimTest,
  },
  {
    descriptionKey: "maxDiffDescription",
    icon: ArrowUpDown,
    titleKey: "maxDiff",
    useCase: UseCase.MaxDiff,
  },
  {
    descriptionKey: "monadicDescription",
    icon: Lightbulb,
    titleKey: "monadic",
    useCase: UseCase.Monadic,
  },
  {
    descriptionKey: "npsDescription",
    icon: Gauge,
    titleKey: "nps",
    useCase: UseCase.NPS,
  },
  {
    descriptionKey: "packagingDescription",
    icon: Package,
    titleKey: "packaging",
    useCase: UseCase.Packaging,
  },
  {
    descriptionKey: "priceElasticityDescription",
    icon: Tag,
    titleKey: "priceElasticity",
    useCase: UseCase.PriceElasticity,
  },
  {
    descriptionKey: "turfDescription",
    icon: Target,
    titleKey: "turf",
    useCase: UseCase.TURF,
  },
] as const;

/** Hook that returns the localized catalog. */
export function useUseCases(): UseCaseEntry[] {
  const { i18n } = useLocalization();
  return useMemo(
    () =>
      USE_CASES_STATIC.map((uc) => ({
        description: i18n.t(`dashboard.useCases.${uc.descriptionKey}`),
        icon: uc.icon,
        slug: getUseCaseSlug(uc.useCase),
        title: i18n.t(`dashboard.useCases.${uc.titleKey}`),
        useCase: uc.useCase,
      })),
    [i18n]
  );
}

/** Hook that returns + writes the user's favourited use cases. */
export function useFavoriteUseCases() {
  const { user } = useUser();

  const favorites = useMemo<UseCase[]>(() => {
    const raw = user?.unsafeMetadata?.favorites;
    if (!Array.isArray(raw)) return [];
    const known = new Set<string>(Object.values(UseCase));
    return raw.filter(
      (v): v is UseCase => typeof v === "string" && known.has(v)
    );
  }, [user?.unsafeMetadata?.favorites]);

  const toggle = useCallback(
    async (useCase: UseCase) => {
      if (!user) return;
      const next = favorites.includes(useCase)
        ? favorites.filter((u) => u !== useCase)
        : [...favorites, useCase];
      await user.update({
        unsafeMetadata: { ...user.unsafeMetadata, favorites: next },
      });
    },
    [favorites, user]
  );

  return { favorites, toggle };
}

/* ── Custom test catalog ─────────────────────────────── */

export interface CustomTestItem {
  icon: LucideIcon;
  id: QuestionType;
  label: string;
  route: string;
  subtitle: string;
}

/** Hook that returns the localized custom-test question-type catalog. */
export function useCustomTests(): CustomTestItem[] {
  const { i18n } = useLocalization();
  return useMemo<CustomTestItem[]>(
    () => [
      {
        icon: CircleDot,
        id: "single_choice",
        label: i18n.t("questions.types.singleChoice"),
        route: "/adhoc/questions?type=single-choice",
        subtitle: i18n.t("dashboard.singleChoiceDescription"),
      },
      {
        icon: CheckSquare,
        id: "multiple_choice",
        label: i18n.t("questions.types.multipleChoice"),
        route: "/adhoc/questions?type=multiple-choice",
        subtitle: i18n.t("dashboard.multipleChoiceDescription"),
      },
      {
        icon: MessageSquare,
        id: "open_ended",
        label: i18n.t("questions.types.openEnded"),
        route: "/adhoc/questions?type=open-ended",
        subtitle: i18n.t("dashboard.openEndedDescription"),
      },
      {
        icon: Star,
        id: "rating_scale",
        label: i18n.t("questions.types.ratingScale"),
        route: "/adhoc/questions?type=rating-scale",
        subtitle: i18n.t("dashboard.ratingScaleDescription"),
      },
      {
        icon: SlidersHorizontal,
        id: "linear_scale",
        label: i18n.t("questions.types.linearScale"),
        route: "/adhoc/questions?type=linear-scale",
        subtitle: i18n.t("dashboard.linearScaleDescription"),
      },
      {
        icon: Gauge,
        id: "nps",
        label: i18n.t("questions.types.nps"),
        route: "/adhoc/questions?type=nps",
        subtitle: i18n.t("dashboard.npsDescription"),
      },
      {
        icon: Tag,
        id: "pricing",
        label: i18n.t("questions.types.pricing"),
        route: "/adhoc/questions?type=pricing",
        subtitle: i18n.t("dashboard.pricingDescription"),
      },
      {
        icon: ListOrdered,
        id: "ranking",
        label: i18n.t("questions.types.ranking"),
        route: "/adhoc/questions?type=ranking",
        subtitle: i18n.t("dashboard.rankingDescription"),
      },
      {
        icon: GripHorizontal,
        id: "single_select_matrix",
        label: i18n.t("questions.types.singleSelectMatrix"),
        route: "/adhoc/questions?type=single-select-matrix",
        subtitle: i18n.t("dashboard.singleSelectMatrixDescription"),
      },
      {
        icon: Grid3X3,
        id: "multi_select_matrix",
        label: i18n.t("questions.types.multiSelectMatrix"),
        route: "/adhoc/questions?type=multi-select-matrix",
        subtitle: i18n.t("dashboard.multiSelectMatrixDescription"),
      },
    ],
    [i18n]
  );
}

/* ── Card ────────────────────────────────────────────── */

interface UseCaseCardProps {
  description: string;
  favorite: boolean;
  icon: LucideIcon;
  onPress: () => void;
  onToggleFavorite: () => void;
  title: string;
}

const UseCaseCard: FC<UseCaseCardProps> = ({
  description,
  favorite,
  icon: Icon,
  onPress,
  onToggleFavorite,
  title,
}) => (
  <Pressable className="lg:h-full" onPress={onPress}>
    <Card className="lg:h-full border border-outline-100 rounded-2xl p-0 hover:shadow-soft-2 hover:-translate-y-0.5 transition-all duration-200">
      {/* Mobile: horizontal list row (icon left, text right). Desktop: stacked
          card (icon on top) so the responsive grid reads as tidy tiles. */}
      <HStack className="p-5 items-center gap-4 lg:flex-col lg:items-start lg:gap-3 lg:h-full">
        <Box className="w-9 h-9 rounded-lg bg-primary-50 items-center justify-center shrink-0">
          <Icon color="rgb(var(--color-primary-600))" size={18} />
        </Box>
        <VStack className="flex-1 gap-1">
          <Text className="text-sm font-semibold text-typography-900">
            {title}
          </Text>
          <Text className="text-xs text-typography-400 leading-relaxed">
            {description}
          </Text>
        </VStack>
        {/* Nested pressable so the bookmark can stop event propagation:
            tapping it toggles favourite, tapping anywhere else opens the
            use case. */}
        <Pressable
          className="p-2 -m-2 lg:absolute lg:top-3 lg:right-3"
          onPress={(event) => {
            event.stopPropagation();
            onToggleFavorite();
          }}
        >
          <Bookmark
            color={
              favorite
                ? "rgb(var(--color-primary-500))"
                : "rgb(var(--color-typography-300))"
            }
            fill={favorite ? "rgb(var(--color-primary-500))" : "transparent"}
            size={18}
          />
        </Pressable>
      </HStack>
    </Card>
  </Pressable>
);

interface CustomTestCardProps {
  icon: LucideIcon;
  onPress: () => void;
  subtitle: string;
  title: string;
}

const CustomTestCard: FC<CustomTestCardProps> = ({
  icon: Icon,
  onPress,
  subtitle,
  title,
}) => (
  <Pressable className="lg:h-full" onPress={onPress}>
    <Card className="lg:h-full border border-outline-100 rounded-2xl p-0 hover:shadow-soft-2 hover:-translate-y-0.5 transition-all duration-200">
      <HStack className="p-5 items-center gap-4 lg:flex-col lg:items-start lg:gap-3 lg:h-full">
        <Box className="w-9 h-9 rounded-lg bg-primary-50 items-center justify-center shrink-0">
          <Icon color="rgb(var(--color-primary-600))" size={18} />
        </Box>
        <VStack className="flex-1 gap-1">
          <Text className="text-sm font-semibold text-typography-900">
            {title}
          </Text>
          <Text className="text-xs text-typography-400 leading-relaxed">
            {subtitle}
          </Text>
        </VStack>
      </HStack>
    </Card>
  </Pressable>
);

/* ── Screen ──────────────────────────────────────────── */

const QuickInsightsScreen: FC = () => {
  const router = useRouter();
  const { i18n, locale } = useLocalization();
  const useCases = useUseCases();
  const customTests = useCustomTests();
  const { favorites, toggle } = useFavoriteUseCases();

  const favoriteUseCases = useMemo(
    () =>
      favorites
        .map((value) => useCases.find((uc) => uc.useCase === value))
        .filter((uc): uc is UseCaseEntry => uc !== undefined),
    [favorites, useCases]
  );

  // The full catalog, alphabetised. Favourites also surface in the dedicated
  // section above, but we intentionally keep them here too so the general list
  // always shows every use case.
  const otherUseCases = useMemo(
    () =>
      [...useCases].sort((a, b) =>
        a.title.localeCompare(b.title, locale, { sensitivity: "base" })
      ),
    [locale, useCases]
  );

  const renderCard = (uc: UseCaseEntry) => (
    <UseCaseCard
      key={uc.useCase}
      description={uc.description}
      favorite={favorites.includes(uc.useCase)}
      icon={uc.icon}
      onPress={() => router.push(`/(dashboard)/quick-insights/${uc.slug}`)}
      onToggleFavorite={() => toggle(uc.useCase)}
      title={uc.title}
    />
  );

  return (
    <Box className="flex-1">
      <Header
        subtitle={i18n.t("dashboard.quickInsightsDescription")}
        title={i18n.t("dashboard.quickInsights")}
      />

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        <VStack className="px-4 lg:px-8 py-6 gap-8">
          {favoriteUseCases.length > 0 && (
            <VStack className="gap-3 lg:gap-5">
              <Text className="text-sm font-semibold text-typography-500 uppercase tracking-wide">
                {i18n.t("dashboard.yourFavorites")}
              </Text>
              <VStack className="gap-3 lg:grid lg:grid-cols-2 xl:grid-cols-3">
                {favoriteUseCases.map(renderCard)}
              </VStack>
            </VStack>
          )}

          {otherUseCases.length > 0 && (
            <VStack className="gap-3 lg:gap-5">
              {favoriteUseCases.length > 0 && (
                <Text className="text-sm font-semibold text-typography-500 uppercase tracking-wide">
                  {i18n.t("dashboard.allUseCases")}
                </Text>
              )}
              <VStack className="gap-3 lg:grid lg:grid-cols-2 xl:grid-cols-3">
                {otherUseCases.map(renderCard)}
              </VStack>
            </VStack>
          )}

          <VStack className="gap-3 lg:gap-5">
            <Text className="text-sm font-semibold text-typography-500 uppercase tracking-wide">
              {i18n.t("dashboard.customTest")}
            </Text>
            <VStack className="gap-3 lg:grid lg:grid-cols-2 xl:grid-cols-3">
              {customTests.map((ct) => (
                <CustomTestCard
                  key={ct.id}
                  icon={ct.icon}
                  onPress={() => router.push(ct.route)}
                  subtitle={ct.subtitle}
                  title={ct.label}
                />
              ))}
            </VStack>
          </VStack>
        </VStack>
      </ScrollView>
    </Box>
  );
};

export default QuickInsightsScreen;
